'use client';

import type { PostMediaItem } from '@ps/api/types';
import { ModerationStatusEnumItem } from '@ps/api/types';
import { ModerationStatus } from '@ps/models/lib/moderation';
import { Hourglass, Info } from 'lucide-react';

import type { PostContentKind } from '../../types';

interface Props {
  /** Post-level moderation status. `Accepted` (or unset) renders nothing. */
  status: ModerationStatus | undefined;
  contentKind: PostContentKind;
  /** Post medias — a rejected gallery counts its rejected items for the reason. */
  medias: PostMediaItem[];
  /** `short` = one-word chip ("Rejected"); `long` = full sentence. */
  format?: 'short' | 'long';
  /** Append the rejected reason to the label (only meaningful when rejected). */
  withRejectedReason?: boolean;
  /**
   * Edit the post — only wired for a REJECTED post, where the badge becomes a
   * tap target ("… Update post and resubmit. →"). Omitted / non-rejected → the
   * badge is inert. 1:1 with legacy `PostApprovalBadge` `onPress`.
   */
  onEdit?: () => void;
}

/** Count of medias the backend rejected — drives the media-gallery reason line. */
function rejectedMediaCount(medias: PostMediaItem[]): number {
  return medias.filter(
    media =>
      media.moderation?.moderationStatus === ModerationStatusEnumItem.Rejected,
  ).length;
}

/** The rejected-post reason sentence (empty for non-rejected posts). */
function rejectedReasonOf(
  contentKind: PostContentKind,
  medias: PostMediaItem[],
): string {
  let reason = '';
  if (contentKind === 'media') {
    const count = rejectedMediaCount(medias);
    if (count > 0) {
      reason = `${count} media ${count === 1 ? 'item was' : 'items were'} rejected. Check the media items below for more details. `;
    }
  }
  if (!reason) {
    reason = 'does not follow content guidelines';
  }
  return reason.trim().replace(/\.+$/, '') + '. Update post and resubmit. →';
}

/**
 * Owner-only moderation badge — surfaces a post held for review ("Under review")
 * or turned down ("Rejected"), never shown for an accepted post. 1:1 with legacy
 * `flows/bio/wall/components/post/approval-badge.tsx`; a rejected badge is a tap
 * target that opens the editor to fix + resubmit.
 */
export function PostApprovalBadge({
  status,
  contentKind,
  medias,
  format = 'short',
  withRejectedReason,
  onEdit,
}: Props) {
  if (status == null || status === ModerationStatus.Accepted) {
    return null;
  }

  const rejected = status === ModerationStatus.Rejected;
  const reason = rejected ? rejectedReasonOf(contentKind, medias) : '';

  const label = rejected
    ? (format === 'short' ? 'Rejected' : 'This post was rejected') +
      (withRejectedReason && reason ? `: ${reason}` : '')
    : format === 'short'
      ? 'Under review'
      : 'This post is under review and will be visible to users once approved.';

  const Icon = rejected ? Info : Hourglass;
  const tone = rejected
    ? 'bg-danger text-content-on-danger'
    : 'bg-warning text-content-on-warning';
  const className = `flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs font-medium ${tone}`;

  const content = (
    <>
      <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{label}</span>
    </>
  );

  if (rejected && onEdit) {
    return (
      <button type="button" onClick={onEdit} className={className}>
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}
